'use client';

import { useEffect, useRef, useState } from 'react';
import { ensureFontReady } from '../lib/tapestryText';
import { packLayout } from '../lib/tapestryPacker';

/**
 * Run the bbox packer client-side after the font and the symbol keyword
 * library are both loaded. Returns an array of placements (or [] while
 * computing). Re-runs when the input state or canvas height changes.
 *
 * `keywordIndex` is the resource-bound keyword index from
 * createSymbolKeywordIndex(resources) — passed in so this hook stays free of
 * any backend coupling.
 *
 * Maintains a `prevPlacements` map across runs so the packer can keep
 * each symbol near its previous position when the layout recomputes —
 * minimizes dramatic relocations as new symbols come in.
 *
 * `fontFamily` is the face the text will be set in (the skin's): the packer
 * measures in it, after it has loaded.
 */
export default function useTapestryLayout(state, canvasHeight, keywordIndex, fontFamily, enabled = true) {
  const [placements, setPlacements] = useState([]);
  const prevPlacementsRef = useRef(new Map());

  useEffect(() => {
    if (!enabled || !canvasHeight || !state || !keywordIndex) return;
    let cancelled = false;
    Promise.all([ensureFontReady(fontFamily), keywordIndex.loadSymbolKeywords()]).then(([, keywordsMap]) => {
      if (cancelled) return;
      const next = packLayout(state, canvasHeight, keywordsMap, prevPlacementsRef.current, fontFamily);
      const nextMap = new Map();
      for (const p of next) nextMap.set(p.symbolId, { x: p.x, y: p.y, side: p.side });
      prevPlacementsRef.current = nextMap;
      setPlacements(next);
    });
    return () => {
      cancelled = true;
    };
  }, [state, canvasHeight, keywordIndex, fontFamily, enabled]);

  return placements;
}
